import React, { useState, useEffect } from 'react';
import { Table, Button, Modal, Form, Input, Select, Switch, Tag, Popconfirm, message } from 'antd';
import { PlusOutlined, DeleteOutlined, EditOutlined } from '@ant-design/icons';
import { getBadgeTypes, createBadgeType, updateBadgeType, deleteBadgeType } from '../api/studentBadges';
import { apiErrorMessage } from '../utils/apiError';

const KIND_LABELS = { persistent: 'Kalıcı Özellik', one_time: 'Geçici Durum' };
const DEFAULT_COLORS = ['#1677ff', '#722ed1', '#eb2f96', '#fa8c16', '#52c41a', '#faad14', '#f5222d', '#13c2c2'];

// Sube muduru de kullandigi icin (Ayarlar sayfasi artik sadece admin-only)
// ConsentTexts.jsx/Notifications.jsx ile ayni desen - kendi sidebar sayfasi.
const StudentBadgeTypes = () => {
  const [types, setTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [form] = Form.useForm();

  const fetchTypes = () => {
    setLoading(true);
    getBadgeTypes({ include_inactive: 1 })
      .then(res => setTypes(res.data || []))
      .catch(() => message.error('Rozet türleri yüklenemedi.'))
      .finally(() => setLoading(false));
  };

  useEffect(() => { fetchTypes(); }, []);

  const openCreate = () => {
    setEditing(null);
    form.resetFields();
    form.setFieldsValue({ kind: 'persistent', color: DEFAULT_COLORS[0] });
    setModalOpen(true);
  };

  const openEdit = (type) => {
    setEditing(type);
    form.setFieldsValue(type);
    setModalOpen(true);
  };

  const handleSave = async (values) => {
    setSaving(true);
    try {
      if (editing) {
        await updateBadgeType(editing.id, values);
        message.success('Rozet türü güncellendi.');
      } else {
        await createBadgeType(values);
        message.success('Rozet türü oluşturuldu.');
      }
      setModalOpen(false);
      fetchTypes();
    } catch (error) {
      message.error(apiErrorMessage(error, 'Kaydedilemedi.'));
    } finally {
      setSaving(false);
    }
  };

  const handleToggleActive = async (type, checked) => {
    try {
      await updateBadgeType(type.id, { is_active: checked });
      message.success(checked ? 'Aktifleştirildi.' : 'Pasifleştirildi.');
      fetchTypes();
    } catch {
      message.error('Durum güncellenemedi.');
    }
  };

  const handleDelete = async (id) => {
    try {
      const res = await deleteBadgeType(id);
      message.success(res.data?.message || 'Silindi.');
      fetchTypes();
    } catch (error) {
      message.error(apiErrorMessage(error, 'Silinemedi.'));
    }
  };

  const columns = [
    {
      title: 'Rozet',
      key: 'name',
      render: (_, r) => <Tag color={r.color}>{r.name}</Tag>,
    },
    {
      title: 'Tür',
      dataIndex: 'kind',
      render: v => KIND_LABELS[v] || v,
    },
    {
      title: 'Aktif',
      key: 'is_active',
      render: (_, r) => (
        <Switch checked={r.is_active} onChange={(checked) => handleToggleActive(r, checked)} />
      ),
    },
    {
      title: '',
      key: 'actions',
      align: 'right',
      render: (_, r) => (
        <div className="flex gap-2 justify-end">
          <Button size="small" icon={<EditOutlined />} onClick={() => openEdit(r)} />
          <Popconfirm title="Bu rozet türü silinsin mi?" onConfirm={() => handleDelete(r.id)} okText="Evet" cancelText="Hayır">
            <Button size="small" danger icon={<DeleteOutlined />} />
          </Popconfirm>
        </div>
      ),
    },
  ];

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-2xl font-bold mb-0">Öğrenci Rozetleri</h2>
        <Button type="primary" icon={<PlusOutlined />} onClick={openCreate}>Yeni Rozet Türü</Button>
      </div>

      <p className="text-gray-500 mb-4">
        Burada tanımlanan rozetler öğrenci profilinde tüm personele görünür, ancak
        yalnızca yöneticiler bir öğrenciye rozet ekleyebilir/kaldırabilir.
      </p>

      <Table rowKey="id" columns={columns} dataSource={types} loading={loading} pagination={false} />

      <Modal
        title={editing ? 'Rozet Türünü Düzenle' : 'Yeni Rozet Türü'}
        open={modalOpen}
        onCancel={() => setModalOpen(false)}
        footer={null}
      >
        <Form form={form} layout="vertical" onFinish={handleSave}>
          <Form.Item name="name" label="Ad" rules={[{ required: true, message: 'Ad giriniz.' }]}>
            <Input placeholder="ör. Otistik" maxLength={60} />
          </Form.Item>
          <Form.Item name="kind" label="Tür" rules={[{ required: true }]}>
            <Select>
              <Select.Option value="persistent">Kalıcı Özellik — hep görünür</Select.Option>
              <Select.Option value="one_time">Geçici Durum — sonra kaldırılır</Select.Option>
            </Select>
          </Form.Item>
          <Form.Item name="color" label="Renk">
            <Select>
              {DEFAULT_COLORS.map(c => (
                <Select.Option key={c} value={c}>
                  <Tag color={c} style={{ marginRight: 4 }}>{c}</Tag>
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
          <div className="flex justify-end gap-2">
            <Button onClick={() => setModalOpen(false)}>İptal</Button>
            <Button type="primary" htmlType="submit" loading={saving}>Kaydet</Button>
          </div>
        </Form>
      </Modal>
    </div>
  );
};

export default StudentBadgeTypes;
